<template>
  <div
    class="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-900"
  >
    <!-- Animated Background -->
    <div class="absolute inset-0 w-full h-full bg-gradient-4">
      <div class="absolute inset-0 w-full h-full">
        <div
          class="absolute top-0 -left-4 w-[500px] h-[500px] bg-[#205072] rounded-full mix-blend-multiply filter blur-[128px] opacity-30 animate-blob"
        ></div>
        <div
          class="absolute top-0 -right-4 w-[500px] h-[500px] bg-[#329C9C] rounded-full mix-blend-multiply filter blur-[128px] opacity-30 animate-blob animation-delay-2000"
        ></div>
        <div
          class="absolute -bottom-8 left-20 w-[500px] h-[500px] bg-teal-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-30 animate-blob animation-delay-4000"
        ></div>
      </div>
    </div>

    <!-- Login Container -->
    <div
      class="relative w-[448px] bg-white/60 rounded-[12px] backdrop-blur-[30px] p-8 space-y-6 border border-white/50 shadow-lg before:absolute before:inset-0 before:bg-linear-to-b before:from-white/10 before:to-transparent before:rounded-[12px] before:-z-10" style="max-height: 95vh;"
    >
      <!-- Logo -->
      <div class="flex justify-center mb-10">
        <img src="@/assets/Logo.svg" alt="CEETRAC Logo" class="h-16 w-auto" />
      </div>

      <!-- Mandanten Modal -->
      <div
        class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-opacity"
      >
        <div 
          class="bg-white/95 rounded-xl shadow-xl w-full max-w-md transform transition-all animate-modal-entry"
        >
          <!-- Modal Header -->
          <div class="px-6 pt-6 pb-4">
            <div class="flex items-center space-x-3 mb-2">
              <div class="p-2 bg-[#329C9C]/10 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#329C9C]" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
              </div>
              <h2 class="text-xl font-semibold text-slate-800">
                {{ t('auth.tenantSelection') }}
              </h2>
            </div>
            <p class="text-sm text-slate-600">
              {{ t('auth.tenantSelectionExplanation') }}
            </p>
          </div>

          <!-- Search Bar - nur anzeigen wenn mehr als 3 Mandanten -->
          <div v-if="(userStore.user?.tenants || []).length > 3" class="px-6 pb-4">
            <div class="relative">
              <input
                v-model="mandantSearch"
                type="text"
                :placeholder="t('auth.searchTenants')"
                class="w-full h-11 pl-11 pr-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-[#329C9C] focus:border-transparent transition-shadow"
              />
              <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Stats Bar - nur anzeigen wenn Suche aktiv -->
          <div v-if="(userStore.user?.tenants || []).length > 3" class="px-6 space-y-4">
            <div class="flex items-center justify-between text-sm text-slate-500 px-1">
              <span class="flex items-center space-x-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                </svg>
                <span>{{ filteredMandanten.length }} von {{ (userStore.user?.tenants || []).length }} {{ mandantSearch ? 'gefunden' : 'verfügbar' }}</span>
              </span>
              <span v-if="mandantSearch" class="text-xs px-2 py-1 bg-slate-100 rounded-full">
                {{ t('common.search') }}: "{{ mandantSearch }}"
              </span>
            </div>
          </div>

          <!-- Mandanten Liste -->
          <div class="px-6 space-y-4 overflow-y-auto">
            <!-- Zuletzt verwendeter Mandant -->
            <div v-if="lastUsedTenant" class="space-y-2">
              <div class="text-xs font-medium text-slate-500 uppercase tracking-wider px-1">{{ t('common.lastUsed') }}</div>
              <div
                class="relative flex items-center p-4 cursor-pointer bg-[#329C9C]/5 border border-[#329C9C]/20 rounded-xl hover:bg-[#329C9C]/10 transition-all duration-200 group"
                :class="{
                  'ring-2 ring-[#329C9C] border-transparent shadow-sm': selectedMandant === lastUsedTenant.tenant,
                }"
                @click="selectedMandant = lastUsedTenant.tenant"
              >
                <input
                  v-model="selectedMandant"
                  type="radio"
                  :name="lastUsedTenant.tenant"
                  :value="lastUsedTenant.tenant"
                  class="h-4 w-4 text-[#329C9C] border-slate-200 focus:ring-[#329C9C]"
                />
                <div class="ml-3 flex-1">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-2">
                      <span class="text-sm font-medium text-slate-800 group-hover:text-slate-900">
                        {{ lastUsedTenant.tenant }}
                      </span>
                      <span class="text-xs text-slate-500 group-hover:text-slate-600">({{ t('common.lastUsed') }})</span>
                    </div>
                    <!-- TODO: is this still required? -->
                    <!-- <span
                      v-if="lastUsedTenant.adminKz"
                      class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#329C9C]/10 text-[#329C9C] group-hover:bg-[#329C9C]/15"
                    >
                      Administrator
                    </span> -->
                  </div>
                </div>
              </div>
            </div>

            <!-- Andere Mandanten -->
            <div v-if="otherFilteredTenants.length > 0" class="space-y-2">
              <div class="text-xs font-medium text-slate-500 uppercase tracking-wider px-1">
                {{ lastUsedTenant ? t('common.furtherTenants') : t('common.availableTenants') }}
              </div>
              <div
                v-for="tenant in otherFilteredTenants"
                :key="tenant.tenant"
                class="relative flex items-center p-4 cursor-pointer bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all duration-200 group"
                :class="{
                  'ring-2 ring-[#329C9C] border-transparent shadow-sm': selectedMandant === tenant.tenant,
                }"
                @click="selectedMandant = tenant.tenant"
              >
                <input
                  v-model="selectedMandant"
                  type="radio"
                  :name="tenant.tenant"
                  :value="tenant.tenant"
                  class="h-4 w-4 text-[#329C9C] border-slate-200 focus:ring-[#329C9C]"
                />
                <div class="ml-3 flex-1">
                  <div class="flex items-center justify-between">
                    <span class="text-sm font-medium text-slate-800 group-hover:text-slate-900">
                      {{ tenant.tenant }}
                    </span>
                    <!-- TODO: is this still required? -->
                    <!-- <span
                      v-if="tenant.adminKz"
                      class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#329C9C]/10 text-[#329C9C] group-hover:bg-[#329C9C]/15"
                    >
                      Administrator
                    </span> -->
                  </div>
                </div>
              </div>
            </div>

            <!-- Keine Ergebnisse -->
            <div 
              v-if="filteredMandanten.length === 0"
              class="text-center py-8 space-y-2"
            >
              <div class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <p class="text-sm font-medium text-slate-900">{{ t('common.noTenantsFound') }}</p>
                <p class="text-sm text-slate-500">{{ t('common.checkSearchInput') }}</p>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="px-6 py-4 mt-4 border-t border-slate-200/80">
            <button
              class="w-full h-11 bg-[#329C9C] text-white rounded-xl font-medium hover:bg-[#205072] focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-[#329C9C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              :disabled="!selectedMandant"
              @click="confirmMandant"
            >
              <span class="flex items-center justify-center space-x-2">
                <span>{{ t('common.selectAndContinue') }}</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { AuthService } from "@/services/authService";
import type { TenantInfo } from "@/types/auth";
import { useI18n } from "vue-i18n";
import { useUserStore } from "@/stores/user";

const authService = new AuthService();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const userStore = useUserStore();

// Hilfsfunktion für die Weiterleitung nach erfolgreicher Anmeldung
const redirectAfterLogin = () => {
  // Wenn keine Weiterleitung gesetzt ist oder es sich um eine Login-Seite handelt
  if (route.path.includes('/auth/login')) {
    router.push(`/en/user/dashboard`);
  } else {
    router.push(`/en/user/dashboard`);
  }
};

const selectedMandant = ref(authService.getTenant() || '');
const mandantSearch = ref("");
const RECENT_MANDANTS_KEY = 'recent_mandants';

// Computed properties für gefilterte Mandanten
const filteredMandanten = computed(() => {
  if (!mandantSearch.value) return userStore.user?.tenants || [];
  const search = mandantSearch.value.toLowerCase();
  return (userStore.user?.tenants || []).filter(m => 
    m.tenant.toLowerCase().includes(search)
  );
});

// Hole den zuletzt verwendeten Mandanten
const getLastUsedMandant = (): string | null => {
  const saved = localStorage.getItem(RECENT_MANDANTS_KEY);
  if (!saved) return null;
  const recentIds: string[] = JSON.parse(saved);
  return recentIds[0] || null;
};

// Computed für den zuletzt verwendeten Mandanten
const lastUsedTenant = computed(() => {
  const lastUsedId = getLastUsedMandant();
  return lastUsedId ? (userStore.user?.tenants || []).find(m => m.tenant === lastUsedId) || null : null;
});

// Gefilterte Mandanten ohne den zuletzt verwendeten
const otherFilteredTenants = computed(() => {
  return filteredMandanten.value.filter(m => 
    !lastUsedTenant.value || m.tenant !== lastUsedTenant.value.tenant
  );
});

// Speichere den ausgewählten Mandanten als zuletzt verwendet
const saveRecentMandant = (mandantId: string) => {
  localStorage.setItem(RECENT_MANDANTS_KEY, JSON.stringify([mandantId]));
};

const confirmMandant = () => {
  if (selectedMandant.value) {
    saveRecentMandant(selectedMandant.value);
    userStore.setTenant(selectedMandant.value);
    redirectAfterLogin();
  }
};
onMounted(() => {
  // confirmMandant();
});
</script>

<style>
.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}

@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

.animate-modal-entry {
  animation: modal-entry 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes modal-entry {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style> 
